import { getLoopProgress, getTimeOfDay, type TimeOfDay } from "../game/atmosphereEngine"

/**
 * The Waning Sun — a diegetic instrument for the one dread the whole game turns
 * on: the sun the Necromancer's ritual will put out on Day 512. The status panel
 * already prints the day counter and the days remaining, but those are numbers;
 * this is the thing a character in the world would actually look up at. An
 * astrolabe-like oculus in which the sun climbs from the dawn of a fresh loop to
 * noon, then sinks and reddens through dusk into an eclipsed ember as the
 * deadline nears — retracting its rays as it goes. It reads off exactly the same
 * loop progress the ambient-light engine does (see atmosphereEngine), so the
 * roundel and the light of the whole room drain in step. On the coronation the
 * sun is restored to full noon: the one moment the dark did not come.
 */

/** A short, wordless-as-possible read on where the light stands. */
const CAPTIONS: Record<TimeOfDay, string> = {
  dawn: "A new loop dawns.",
  day: "The light holds.",
  dusk: "The light is going.",
  night: "The long dark gathers.",
}

// Geometry of the oculus. The sun rides an arc whose centre sits on the horizon
// line, so progress 0 puts it on the left horizon (dawn), 0.5 at the zenith
// (noon), and 1 back down on the right horizon (the light all but gone).
const CX = 110
const HORIZON_Y = 92
const ARC_R = 46
const SUN_R = 13

interface SunGeometry {
  x: number
  y: number
  /** How far above the horizon, 0..1 — drives ray reach and glow. */
  height: number
}

function sunAt(progress: number): SunGeometry {
  const angle = Math.PI * (1 - progress)
  return {
    x: CX + ARC_R * Math.cos(angle),
    y: HORIZON_Y - ARC_R * Math.sin(angle),
    height: Math.sin(angle),
  }
}

interface CelestialDialProps {
  currentDay: number
  /** The coronation: the sun held. Show it restored to full noon. */
  restored?: boolean
}

export function CelestialDial({ currentDay, restored = false }: CelestialDialProps) {
  const progress = restored ? 0.5 : getLoopProgress(currentDay)
  const phase: TimeOfDay = restored ? "day" : getTimeOfDay(currentDay)
  const sun = sunAt(progress)

  // Rays retract as the sun sinks: full spokes at noon, mere stubs at the
  // horizon, so the disc visibly loses its reach as the light goes.
  const rayCount = 12
  const rayInner = SUN_R + 3
  const rayOuter = SUN_R + 3 + 9 * sun.height
  const rays = Array.from({ length: rayCount }, (_, i) => {
    const a = (i / rayCount) * Math.PI * 2
    const cos = Math.cos(a)
    const sin = Math.sin(a)
    return {
      x1: sun.x + rayInner * cos,
      y1: sun.y + rayInner * sin,
      x2: sun.x + rayOuter * cos,
      y2: sun.y + rayOuter * sin,
    }
  })

  // Ticks around the ring, like the hour-marks of a sundial. Purely decorative.
  const ticks = Array.from({ length: 24 }, (_, i) => {
    const a = (i / 24) * Math.PI * 2
    const long = i % 6 === 0
    const r1 = long ? 50 : 52
    const r2 = 55
    return {
      x1: CX + r1 * Math.cos(a),
      y1: 66 + r1 * Math.sin(a),
      x2: CX + r2 * Math.cos(a),
      y2: 66 + r2 * Math.sin(a),
      long,
    }
  })

  const caption = restored ? "The sun holds. The dark did not come." : CAPTIONS[phase]

  return (
    <figure
      className="celestial-dial"
      data-phase={restored ? "restored" : phase}
      aria-hidden="true"
    >
      <svg viewBox="0 0 220 132" role="presentation">
        <defs>
          <clipPath id="dial-oculus">
            <circle cx={CX} cy="66" r="55" />
          </clipPath>
          <radialGradient id="dial-sky" cx="50%" cy="86%" r="90%">
            <stop className="dial-sky-low" offset="0%" />
            <stop className="dial-sky-high" offset="100%" />
          </radialGradient>
          <radialGradient id="dial-glow" cx="50%" cy="50%" r="50%">
            <stop className="dial-glow-in" offset="0%" />
            <stop className="dial-glow-out" offset="100%" />
          </radialGradient>
        </defs>

        {/* The sky, and everything that rides in it, kept inside the oculus. */}
        <g clipPath="url(#dial-oculus)">
          <rect x="0" y="0" width="220" height="132" fill="url(#dial-sky)" />

          {/* A few fixed stars, revealed only as the sky darkens toward night. */}
          <g className="dial-stars">
            <circle cx="78" cy="34" r="0.9" />
            <circle cx="140" cy="28" r="1.1" />
            <circle cx="158" cy="48" r="0.8" />
            <circle cx="64" cy="52" r="0.7" />
            <circle cx="120" cy="46" r="0.8" />
          </g>

          {/* The glow the sun casts into the surrounding sky. */}
          <circle
            className="dial-halo"
            cx={sun.x}
            cy={sun.y}
            r={SUN_R + 20}
            fill="url(#dial-glow)"
          />

          <g className="dial-rays" strokeLinecap="round">
            {rays.map((r, i) => (
              <line key={i} x1={r.x1} y1={r.y1} x2={r.x2} y2={r.y2} />
            ))}
          </g>

          {/* The disc itself, and the shadow that eclipses it as the dark comes:
              the shadow rides in from the upper-right, biting a crescent out of
              the sun as night falls. */}
          <circle className="dial-sun" cx={sun.x} cy={sun.y} r={SUN_R} />
          <circle
            className="dial-eclipse"
            cx={sun.x + SUN_R * 0.62}
            cy={sun.y - SUN_R * 0.42}
            r={SUN_R}
          />

          {/* The horizon the sun rises from and sets into. */}
          <line
            className="dial-horizon"
            x1="55"
            y1={HORIZON_Y}
            x2="165"
            y2={HORIZON_Y}
          />
        </g>

        {/* The instrument's frame: an outer ring and its hour-marks. */}
        <g className="dial-ticks" strokeLinecap="round">
          {ticks.map((t, i) => (
            <line
              key={i}
              x1={t.x1}
              y1={t.y1}
              x2={t.x2}
              y2={t.y2}
              className={t.long ? "dial-tick-long" : ""}
            />
          ))}
        </g>
        <circle className="dial-ring" cx={CX} cy="66" r="55" fill="none" />
        <circle className="dial-ring-inner" cx={CX} cy="66" r="50" fill="none" />
      </svg>

      <figcaption className="dial-caption">{caption}</figcaption>
    </figure>
  )
}
